import { useMemo, useState } from "react";
import { ChevronDown, Clock3, Tag as TagIcon, User as UserIcon, X } from "lucide-react";
import { Agent } from "@/types/agent/agent";
import { CONVERSATION_TIME_RANGE_OPTIONS } from "@/constants/conversationFilters";
import { GenieFilterAvatar, GenieFilterPicker } from "@/components/conversation/GenieFilterPicker";
import { DEFAULT_BRANDING } from "@/types/branding";
import { cn } from "@/lib/utils";
import { ConversationChannel, ConversationType } from "@/types/conversation/conversation";
import { CHANNEL_FILTER_OPTIONS, CONVERSATION_TYPE_FILTER_OPTIONS } from "@/components/conversation/conversationListShared";

interface ConversationFiltersProps {
  agents: Agent[];
  selectedAgent: string | null;
  onSelectedAgentChange: (agentId: string | null) => void;
  timeRange: string;
  onTimeRangeChange: (range: string) => void;
  showOwner?: boolean;
  selectedChannel?: ConversationChannel | null;
  onSelectedChannelChange?: (channel: ConversationChannel | null) => void;
  selectedType?: ConversationType | null;
  onSelectedTypeChange?: (type: ConversationType | null) => void;
  selectedOwner?: string | null;
  onSelectedOwnerChange?: (ownerId: string | null) => void;
  hasActiveFilters?: boolean;
  onClearAll?: () => void;
}

const EXTRA_FILTER_GRID_COLS = [
  "md:grid-cols-[minmax(0,1fr)_13rem]",
  "md:grid-cols-[minmax(0,1fr)_11rem_13rem]",
  "md:grid-cols-[minmax(0,1fr)_11rem_11rem_13rem]",
  "md:grid-cols-[minmax(0,1fr)_11rem_11rem_11rem_13rem]",
] as const;

export const ConversationFilters = ({
  agents,
  selectedAgent,
  onSelectedAgentChange,
  timeRange,
  onTimeRangeChange,
  showOwner = false,
  selectedChannel = null,
  onSelectedChannelChange,
  selectedType = null,
  onSelectedTypeChange,
  selectedOwner = null,
  onSelectedOwnerChange,
  hasActiveFilters = false,
  onClearAll,
}: ConversationFiltersProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedGenie = useMemo(
    () => agents.find((agent) => agent.id === selectedAgent) ?? null,
    [agents, selectedAgent],
  );

  const owners = useMemo(() => {
    const byId = new Map<string, { id: string; name: string }>();
    agents.forEach((agent) => {
      const owner = agent.owner;
      if (owner?.id && !byId.has(owner.id)) {
        byId.set(owner.id, { id: owner.id, name: owner.full_name || owner.email || "Unknown owner" });
      }
    });
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [agents]);

  const getBrandColor = (agent: Agent) =>
    agent.branding?.primaryColor || DEFAULT_BRANDING.primaryColor;

  return (
    <div className="bg-white rounded-2xl border border-genie-border shadow-sm p-5 md:p-6 mb-6">
      {onClearAll && hasActiveFilters && (
        <div className="flex justify-end mb-3">
          <button
            type="button"
            onClick={onClearAll}
            className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-genie-primary transition-colors"
          >
            <X className="h-3.5 w-3.5" />
            Clear all filters
          </button>
        </div>
      )}
      <div
        className={cn(
          "grid grid-cols-1 gap-4 md:gap-6",
          EXTRA_FILTER_GRID_COLS[
            [Boolean(onSelectedChannelChange), Boolean(onSelectedTypeChange), Boolean(onSelectedOwnerChange && owners.length > 0)].filter(
              Boolean,
            ).length
          ],
        )}
      >
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Genie</label>
          <GenieFilterPicker
            agents={agents}
            value={selectedAgent}
            onChange={onSelectedAgentChange}
            showOwner={showOwner}
            onOpenChange={setIsOpen}
          >
            <button
              type="button"
              className="w-full min-h-12 px-3 rounded-xl border bg-white flex items-center gap-3 text-left transition-all hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-genie-primary"
              style={{ borderColor: selectedGenie ? `${getBrandColor(selectedGenie)}66` : "#E5EAF0" }}
              aria-label={`Filter conversations by genie: ${selectedGenie?.name || "All Genies"}`}
            >
              <GenieFilterAvatar agent={selectedGenie} size={32} />
              <span className="min-w-0 flex-1 truncate text-sm font-semibold text-[#222B45]">
                {selectedGenie?.name || "All Genies"}
              </span>
              <ChevronDown className={cn("h-4 w-4 shrink-0 text-gray-400 transition-transform", isOpen && "rotate-180")} />
            </button>
          </GenieFilterPicker>
        </div>

        {onSelectedChannelChange && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Channel</label>
            <div className="relative">
              <TagIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <select
                value={selectedChannel ?? "all"}
                onChange={(e) =>
                  onSelectedChannelChange(e.target.value === "all" ? null : (e.target.value as ConversationChannel))
                }
                className="w-full appearance-none rounded-xl border border-genie-border bg-white py-3 pl-10 pr-9 text-sm font-medium text-[#222B45] transition-colors focus:outline-none focus:ring-2 focus:ring-genie-primary"
              >
                <option value="all">All channels</option>
                {CHANNEL_FILTER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            </div>
          </div>
        )}

        {onSelectedTypeChange && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Type</label>
            <div className="relative">
              <TagIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <select
                value={selectedType ?? "all"}
                onChange={(e) => onSelectedTypeChange(e.target.value === "all" ? null : (e.target.value as ConversationType))}
                className="w-full appearance-none rounded-xl border border-genie-border bg-white py-3 pl-10 pr-9 text-sm font-medium text-[#222B45] transition-colors focus:outline-none focus:ring-2 focus:ring-genie-primary"
              >
                <option value="all">All types</option>
                {CONVERSATION_TYPE_FILTER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            </div>
          </div>
        )}

        {onSelectedOwnerChange && owners.length > 0 && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Owner</label>
            <div className="relative">
              <UserIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <select
                value={selectedOwner ?? "all"}
                onChange={(e) => onSelectedOwnerChange(e.target.value === "all" ? null : e.target.value)}
                className="w-full appearance-none rounded-xl border border-genie-border bg-white py-3 pl-10 pr-9 text-sm font-medium text-[#222B45] transition-colors focus:outline-none focus:ring-2 focus:ring-genie-primary"
              >
                <option value="all">All owners</option>
                {owners.map((owner) => (
                  <option key={owner.id} value={owner.id}>
                    {owner.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Time range</label>
          <div className="relative">
            <Clock3 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <select
              value={timeRange}
              onChange={(e) => onTimeRangeChange(e.target.value)}
              className="w-full appearance-none rounded-xl border border-genie-border bg-white py-3 pl-10 pr-9 text-sm font-medium text-[#222B45] transition-colors focus:outline-none focus:ring-2 focus:ring-genie-primary"
            >
              {CONVERSATION_TIME_RANGE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          </div>
        </div>
      </div>
    </div>
  );
};
